import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as M from '@mantine/core';
import dayjs from 'dayjs';

import {
  fetchPublicHolidays,
  holidayCountries,
  PublicHoliday,
} from './holidays';

interface Props {
  country: string;
  date: string;
  onSelect: (holiday: PublicHoliday) => void;
  onClose: () => void;
}

export default function HolidayBrowser({
  country,
  date,
  onSelect,
  onClose,
}: Props) {
  const [selectedCountry, setCountry] = useState(country);
  const [year, setYear] = useState<number | ''>(
    date ? Number(date.slice(0, 4)) : new Date().getFullYear(),
  );
  const [search, setSearch] = useState('');
  const validYear =
    typeof year === 'number' &&
    Number.isInteger(year) &&
    year >= 1900 &&
    year <= 2100;
  const holidays = useQuery(
    ['publicHolidays', selectedCountry, year],
    () => fetchPublicHolidays(selectedCountry, Number(year)),
    { enabled: validYear, staleTime: 60 * 60 * 1000, retry: false },
  );
  const results = (holidays.data ?? []).filter((holiday) =>
    [holiday.name, holiday.localName, holiday.date, ...holiday.states]
      .join(' ')
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );

  return (
    <M.Modal
      opened
      onClose={onClose}
      title="Choose a public holiday"
      size="lg"
      centered
    >
      <M.Stack>
        <M.Text size="sm" color="dimmed">
          Choose a holiday to fill the closure form. Public holidays do not
          always mean schools are closed; review the preview before applying.
        </M.Text>
        <M.Group grow align="flex-start">
          <M.Select
            label="Holiday country"
            data={holidayCountries}
            value={selectedCountry}
            onChange={(value) => {
              if (value) setCountry(value);
            }}
          />
          <M.NumberInput
            label="Year"
            value={year}
            min={1900}
            max={2100}
            precision={0}
            onChange={setYear}
            error={
              !validYear ? 'Enter a year between 1900 and 2100.' : undefined
            }
          />
        </M.Group>
        <M.TextInput
          label="Find a holiday"
          placeholder="Search name, date, state or province"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />
        {validYear && holidays.isLoading && (
          <M.Group role="status">
            <M.Loader size="sm" />
            <M.Text>Loading holidays…</M.Text>
          </M.Group>
        )}
        {holidays.isError && (
          <M.Alert color="red" title="Holidays unavailable">
            Could not load public holidays. Try again or enter a date manually.
            <M.Button
              variant="subtle"
              size="xs"
              onClick={() => void holidays.refetch()}
            >
              Retry holidays
            </M.Button>
          </M.Alert>
        )}
        {validYear && holidays.isSuccess && (
          <M.ScrollArea.Autosize mah={400}>
            <M.Stack spacing="xs">
              {!results.length && (
                <M.Text color="dimmed" role="status">
                  {holidays.data.length
                    ? 'No holidays match your search.'
                    : 'No public holidays found for this country and year.'}
                </M.Text>
              )}
              {results.map((holiday, index) => (
                <M.Paper
                  withBorder
                  p="sm"
                  radius="md"
                  key={`${holiday.date}-${holiday.name}-${index}`}
                >
                  <M.Group position="apart" noWrap align="flex-start">
                    <div>
                      <M.Text weight={600}>{holiday.name}</M.Text>
                      <M.Text size="sm">
                        {dayjs(holiday.date).format('ddd, MMM D, YYYY')}
                      </M.Text>
                      <M.Text size="xs" color="dimmed">
                        {holiday.states.length
                          ? `Regions: ${holiday.states.join(', ')}`
                          : 'Nationwide'}
                      </M.Text>
                    </div>
                    <M.Button
                      size="xs"
                      variant="light"
                      aria-label={`Select ${holiday.name} on ${holiday.date}`}
                      onClick={() => onSelect(holiday)}
                    >
                      Select
                    </M.Button>
                  </M.Group>
                </M.Paper>
              ))}
            </M.Stack>
          </M.ScrollArea.Autosize>
        )}
        <M.Text size="xs" color="dimmed">
          Source:{' '}
          <M.Anchor
            href="https://date.nager.at"
            target="_blank"
            rel="noreferrer"
            size="xs"
          >
            Nager.Date
          </M.Anchor>
          , also used to validate closure previews. School-specific breaks are
          not included.
        </M.Text>
      </M.Stack>
    </M.Modal>
  );
}
